import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, Phone, ChevronDown } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { SERVICES } from "@/data/services";
import { CONDITIONS } from "@/data/conditions";
import { useLocalize } from "@/data/localize";
import { CLINIC_PHONE } from "@/lib/mailto";
import { ScopeLogo } from "@/components/brand/ScopeLogo";
import { SiteSearch } from "@/components/layout/SiteSearch";
import { LanguageSwitcher } from "@/components/layout/LanguageSwitcher";

function useTopLinks() {
    const { t } = useTranslation();
    return [
        { to: "/about", label: t("nav.about") },
        { to: "/virtual-consultations", label: t("nav.virtualCare") },
        { to: "/technology", label: t("nav.technology") },
        { to: "/preventive-care", label: t("nav.preventiveCare") },
    ];
}

function BrandMark() {
    return (
        <Link to="/" className="flex items-center gap-2.5" data-testid="brand-mark">
            <ScopeLogo size={40} />
            <span className="leading-tight">
                <span className="block font-display text-base sm:text-lg font-semibold text-[color:var(--brand-navy)]">
                    Doctor Kichian's
                </span>
                <span className="block text-[11px] sm:text-xs font-medium tracking-[0.14em] uppercase text-[color:var(--brand-teal-2)]">
                    Gastroenterology
                </span>
            </span>
        </Link>
    );
}

export function Header() {
    const { pathname } = useLocation();
    const { t } = useTranslation();
    const topLinks = useTopLinks();
    const [open, setOpen] = React.useState(false);
    React.useEffect(() => setOpen(false), [pathname]);

    return (
        <header
            data-testid="site-header"
            className="sticky top-0 z-50 border-b border-[color:var(--border-default)] bg-white/85 backdrop-blur supports-[backdrop-filter]:bg-white/70"
        >
            <div className="container-page flex h-16 items-center justify-between gap-3">
                <BrandMark />

                {/* Desktop nav */}
                <nav className="hidden lg:flex items-center gap-1">
                    <ServicesMenu />
                    <ConditionsMenu />
                    <ResourcesMenu />
                    {topLinks.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                                `px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                                    isActive
                                        ? "text-[color:var(--brand-teal-2)] bg-[color:var(--brand-teal-soft)]"
                                        : "text-[color:var(--text-primary)] hover:bg-[color:var(--bg-subtle)]"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-2">
                    <LanguageSwitcher compact />
                    <Button asChild data-testid="header-phone-cta">
                        <a
                            href={`tel:${CLINIC_PHONE.replace(/[^\d+]/g, "")}`}
                            className="gap-1.5"
                        >
                            <Phone className="h-4 w-4" />
                            {CLINIC_PHONE}
                        </a>
                    </Button>
                </div>

                {/* Mobile trigger */}
                <div className="lg:hidden flex items-center gap-2">
                    <LanguageSwitcher compact />
                    <Button asChild size="sm" data-testid="header-phone-cta-mobile">
                        <a href={`tel:${CLINIC_PHONE.replace(/[^\d+]/g, "")}`} className="gap-1.5">
                            <Phone className="h-4 w-4" />
                            {t("cta.call")}
                        </a>
                    </Button>
                    <Sheet open={open} onOpenChange={setOpen}>
                        <SheetTrigger asChild>
                            <Button variant="outline" size="icon" aria-label={t("nav.openMenu")} data-testid="nav-mobile-menu">
                                <Menu className="h-5 w-5" />
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="right" className="w-[86%] sm:w-96">
                            <SheetHeader>
                                <SheetTitle className="text-left font-display text-[color:var(--brand-navy)]">
                                    {t("nav.navigate")}
                                </SheetTitle>
                            </SheetHeader>
                            <div className="mt-4">
                                <SiteSearch variant="full" onNavigate={() => setOpen(false)} />
                            </div>
                            <div className="mt-4">
                                <MobileAccordion setOpen={setOpen} />
                                <div className="mt-4 flex flex-col gap-1">
                                    {topLinks.map((l) => (
                                        <MobileLink key={l.to} to={l.to} label={l.label} />
                                    ))}
                                </div>
                                <div className="mt-6 flex flex-col gap-2">
                                    <a
                                        href={`tel:${CLINIC_PHONE.replace(/[^\d+]/g, "")}`}
                                        className="flex items-center justify-center gap-2 rounded-lg bg-[color:var(--brand-navy)] px-3 py-2.5 text-sm font-semibold text-white hover:bg-[color:var(--brand-navy-2)]"
                                        data-testid="mobile-phone-cta"
                                    >
                                        <Phone className="h-4 w-4" />
                                        {CLINIC_PHONE}
                                    </a>
                                </div>
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>

            {/* Desktop search row */}
            <div className="hidden md:block border-t border-[color:var(--border-default)] bg-white/70 backdrop-blur">
                <div className="container-page py-2.5 flex items-center justify-center">
                    <SiteSearch />
                </div>
            </div>
        </header>
    );
}

function MobileAccordion() {
    const { t } = useTranslation();
    const { localizeList } = useLocalize();
    const services = localizeList(SERVICES);
    const conditions = localizeList(CONDITIONS);
    return (
        <Accordion type="multiple" className="w-full">
            <AccordionItem value="services">
                <AccordionTrigger>{t("nav.services")}</AccordionTrigger>
                <AccordionContent>
                    <div className="flex flex-col gap-1">
                        <MobileLink to="/services" label={t("nav.allServices")} />
                        {services.map((s) => (
                            <MobileLink key={s.slug} to={`/services/${s.slug}`} label={s.name} />
                        ))}
                    </div>
                </AccordionContent>
            </AccordionItem>
            <AccordionItem value="conditions">
                <AccordionTrigger>{t("nav.conditions")}</AccordionTrigger>
                <AccordionContent>
                    <div className="flex flex-col gap-1">
                        <MobileLink to="/conditions" label={t("nav.allConditions")} />
                        {conditions.map((c) => (
                            <MobileLink key={c.slug} to={`/conditions/${c.slug}`} label={c.name} />
                        ))}
                    </div>
                </AccordionContent>
            </AccordionItem>
            <AccordionItem value="resources">
                <AccordionTrigger>{t("nav.patientResources")}</AccordionTrigger>
                <AccordionContent>
                    <div className="flex flex-col gap-1">
                        <MobileLink to="/resources" label={t("nav.patientResourcesHub")} />
                        <MobileLink to="/resources/bowel-prep-guide" label={t("footer.links.bowelPrepGuide")} />
                        <MobileLink to="/faq" label={t("nav.faq")} />
                        <MobileLink to="/education" label={t("nav.education")} />
                        <MobileLink to="/referrals" label={t("nav.referringPhysicians")} />
                    </div>
                </AccordionContent>
            </AccordionItem>
        </Accordion>
    );
}

function MobileLink({ to, label }) {
    return (
        <Link
            to={to}
            className="block rounded-lg px-3 py-2 text-sm text-[color:var(--text-primary)] hover:bg-[color:var(--bg-subtle)]"
        >
            {label}
        </Link>
    );
}

function MenuTrigger({ label, testid }) {
    return (
        <DropdownMenuTrigger asChild>
            <button
                data-testid={testid}
                className="flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg text-[color:var(--text-primary)] hover:bg-[color:var(--bg-subtle)]"
            >
                {label}
                <ChevronDown className="h-3.5 w-3.5 opacity-70" />
            </button>
        </DropdownMenuTrigger>
    );
}

function ServicesMenu() {
    const { t } = useTranslation();
    const { localizeList } = useLocalize();
    const services = localizeList(SERVICES);
    return (
        <DropdownMenu>
            <MenuTrigger label={t("nav.services")} testid="nav-services-dropdown" />
            <DropdownMenuContent align="start" className="w-[520px] p-3">
                <DropdownMenuLabel className="px-2 pt-1 text-xs uppercase tracking-wider text-[color:var(--text-soft)]">
                    {t("nav.whatWeOffer")}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <div className="grid grid-cols-2 gap-1">
                    {services.map((s) => (
                        <DropdownMenuItem asChild key={s.slug}>
                            <Link
                                to={`/services/${s.slug}`}
                                className="block rounded-lg px-3 py-2 hover:bg-[color:var(--bg-subtle)]"
                            >
                                <div className="text-sm font-semibold text-[color:var(--brand-navy)]">{s.name}</div>
                                <div className="text-xs text-[color:var(--text-soft)] line-clamp-1">{s.tagline}</div>
                            </Link>
                        </DropdownMenuItem>
                    ))}
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                    <Link to="/services" className="text-sm font-semibold text-[color:var(--brand-teal-2)]">
                        {t("nav.viewAllServices")}
                    </Link>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

function ConditionsMenu() {
    const { t } = useTranslation();
    const { localizeList } = useLocalize();
    const conditions = localizeList(CONDITIONS);
    return (
        <DropdownMenu>
            <MenuTrigger label={t("nav.conditions")} testid="nav-conditions-dropdown" />
            <DropdownMenuContent align="start" className="w-[520px] p-3">
                <DropdownMenuLabel className="px-2 pt-1 text-xs uppercase tracking-wider text-[color:var(--text-soft)]">
                    {t("nav.conditionsWeTreat")}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <div className="grid grid-cols-2 gap-1 max-h-80 overflow-y-auto pr-1">
                    {conditions.map((c) => (
                        <DropdownMenuItem asChild key={c.slug}>
                            <Link
                                to={`/conditions/${c.slug}`}
                                className="block rounded-lg px-3 py-2 hover:bg-[color:var(--bg-subtle)]"
                            >
                                <div className="text-sm font-semibold text-[color:var(--brand-navy)]">{c.name}</div>
                                <div className="text-xs text-[color:var(--text-soft)]">{c.category}</div>
                            </Link>
                        </DropdownMenuItem>
                    ))}
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                    <Link to="/conditions" className="text-sm font-semibold text-[color:var(--brand-teal-2)]">
                        {t("nav.viewAllConditions")}
                    </Link>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

function ResourcesMenu() {
    const { t } = useTranslation();
    const items = [
        { to: "/resources", label: t("nav.patientResourcesHub"), desc: t("nav.prepGuidesEssentials") },
        { to: "/faq", label: t("nav.faqLong"), desc: t("nav.commonQuestionsAnswered") },
        { to: "/education", label: t("nav.education"), desc: t("nav.educationDesc") },
        { to: "/referrals", label: t("nav.referringPhysicians"), desc: t("nav.referAPatient") },
    ];
    return (
        <DropdownMenu>
            <MenuTrigger label={t("nav.resources")} testid="nav-resources-dropdown" />
            <DropdownMenuContent align="start" className="w-[380px] p-3">
                <DropdownMenuLabel className="px-2 pt-1 text-xs uppercase tracking-wider text-[color:var(--text-soft)]">
                    {t("nav.guidesAndInformation")}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <div className="flex flex-col gap-1">
                    {items.map((i) => (
                        <DropdownMenuItem asChild key={i.to}>
                            <Link to={i.to} className="block rounded-lg px-3 py-2 hover:bg-[color:var(--bg-subtle)]">
                                <div className="text-sm font-semibold text-[color:var(--brand-navy)]">{i.label}</div>
                                <div className="text-xs text-[color:var(--text-soft)]">{i.desc}</div>
                            </Link>
                        </DropdownMenuItem>
                    ))}
                </div>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
